import { RecordUtils } from "@rsm-hcd/javascript-core";
import { Record } from "immutable";
import { CitationItem } from "models/interfaces/citations/citation-item";
import SectionRecord from "../section-record";
import TableRecord from "../table-record";
import AnnexRecord from "../annex-record";
import ArticleRecord from "../article-record";
import { PartRecord } from "internal";
import PublicationRecord from "../publication-record";
import { CaapsTypes } from "constants/caaps-types";

const defaultValues: CitationItem =
    RecordUtils.defaultValuesFactory<CitationItem>({
        type: undefined,
        citation: undefined,
        publication: undefined,
    });

const recordMap = {
    [CaapsTypes.Section]: (c: CitationItem["citation"]) =>
        RecordUtils.ensureRecord(c, SectionRecord),
    [CaapsTypes.Table]: (c: CitationItem["citation"]) =>
        RecordUtils.ensureRecord(c, TableRecord),
    [CaapsTypes.Annex]: (c: CitationItem["citation"]) =>
        RecordUtils.ensureRecord(c, AnnexRecord),
    [CaapsTypes.Article]: (c: CitationItem["citation"]) =>
        RecordUtils.ensureRecord(c, ArticleRecord),
    [CaapsTypes.Part]: (c: CitationItem["citation"]) =>
        RecordUtils.ensureRecord(c, PartRecord),
} as const;

export default class CitationItemRecord
    extends Record(defaultValues)
    implements CitationItem
{
    constructor(params?: CitationItem) {
        if (params == null) {
            params = Object.assign({}, defaultValues);
        }

        if (params.type != null && params.citation != null) {
            const ensureRecord = recordMap[params.type];

            if (ensureRecord != null) {
                params.citation = ensureRecord(params.citation);
            }
        }

        if (params.publication != null) {
            params.publication = RecordUtils.ensureRecord(
                params.publication,
                PublicationRecord
            );
        }

        super(params);
    }

    public getRoute(): string {
        if (this.isSectionOrTable(this.citation)) {
            return this.citation?.getRoute(this.publication?.type);
        }
        return this.citation?.getRoute();
    }

    public getTitle(): string {
        if (this.isSectionOrTable(this.citation)) {
            return this.citation?.getFullyQualifiedDisplayTitle();
        } else {
            return this.citation?.getDisplayTitle();
        }
    }

    public getPublicationTitle(): string {
        return this.publication?.getDisplayTitle() ?? "";
    }

    public getBody() {
        return this.citation?.getBody();
    }

    public isSection(
        citation: CitationItem["citation"]
    ): citation is SectionRecord {
        return this.type === CaapsTypes.Section;
    }

    public isTable(
        citation: CitationItem["citation"]
    ): citation is TableRecord {
        return this.type === CaapsTypes.Table;
    }

    public isSectionOrTable(
        citation: CitationItem["citation"]
    ): citation is SectionRecord | TableRecord {
        return this.isSection(citation) || this.isTable(citation);
    }
}
